import { ICheckinLog } from './types';

/**
 * 출석 로그 기준 회원 상태 라벨.
 * - enrolled: 유효 이용권(시설) 또는 잔여 횟수 있는 PT 수강권 보유
 * - visitor: 회원 식별 없는 방문객 메모/관리자 호출 기록
 * - no_pass: 체크인은 됐지만 현재 유효한 이용권/PT 없음 (원인: rapport만 기준으로 통과)
 *
 * 왜: member.memberType은
 *   - 기본값이 'original'이라 "PT 앱 미가입 + 이용권도 없는 회원"도 'original'로 저장되고
 *   - 'public'은 "퍼블릭(시설이용권) 회원"이라는 비즈니스 의미라 "미등록 신규"가 아님
 *   둘 다 "수강 여부" 판정 근거로 쓸 수 없음.
 *   백엔드가 이미 체크인 시점 스냅샷(activeSubscriptions/activeMemberships)을
 *   로그에 저장해두었으므로 그걸로 판정한다.
 */
export type EnrollmentStatus = 'enrolled' | 'visitor' | 'no_pass';
export type CheckinMemberDisplayStatus = EnrollmentStatus | 'public';

type EnrollmentLog = Pick<
    ICheckinLog,
    'memberId' | 'memberType' | 'method' | 'activeSubscriptions' | 'activeMemberships'
>;

export const getEnrollmentStatus = (log: EnrollmentLog): EnrollmentStatus => {
    if (log.method === 'visitor_memo' || log.method === 'admin_call') return 'visitor';
    if (!log.memberId) return 'visitor';

    const hasActiveSubscription = (log.activeSubscriptions?.length ?? 0) > 0;
    const hasActiveMembership = (log.activeMemberships ?? []).some(
        (m) => (m?.remainSession ?? 0) > 0,
    );

    if (hasActiveSubscription || hasActiveMembership) return 'enrolled';
    return 'no_pass';
};

/**
 * 출석 관리 화면용 회원 라벨.
 * - public: 퍼블릭 회원이 유효 이용권으로 출석한 경우
 * - enrolled/visitor/no_pass: 기존 운영 라벨 유지
 *
 * 비고:
 * - non-public 값(original/temp)은 기본값/운영 이력 영향이 있어 단독 판정 근거로 쓰지 않는다.
 * - public은 비즈니스 의미가 명확하므로, 유효 출석(enrolled)에 한해 퍼블릭 회원으로 노출한다.
 */
export const getCheckinMemberDisplayStatus = (log: EnrollmentLog): CheckinMemberDisplayStatus => {
    const status = getEnrollmentStatus(log);
    if (status === 'enrolled' && log.memberType === 'public') return 'public';
    return status;
};
